import React from 'react';

const Privacy = () => {
  return (
    <div className="legal-page-container">
      <div className="legal-content">
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last Updated: December 2025</p>

        <section className="legal-section">
          <h2 className="legal-heading">Introduction</h2>
          <p className="legal-text">
            Find Clarity ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services in India.
          </p>
          <p className="legal-text">
            By using Find Clarity, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Information We Collect</h2>
          
          <h3 className="legal-subheading">Personal Information</h3>
          <p className="legal-text">When you use Find Clarity, we may collect:</p>
          <ul className="legal-list">
            <li>Email address (if you choose to create an account)</li>
            <li>Name or username</li>
            <li>Device information (device type, operating system, unique device identifiers)</li>
          </ul>

          <h3 className="legal-subheading">Usage Information</h3>
          <p className="legal-text">We automatically collect certain information when you use the app:</p>
          <ul className="legal-list">
            <li>Questions and conversations you have with the AI</li>
            <li>App usage patterns and feature interactions</li>
            <li>Time and date of app usage</li>
            <li>Technical data including IP address, browser type, and crash reports</li>
          </ul>

          <h3 className="legal-subheading">Sensitive Personal Data</h3>
          <p className="legal-text">
            Given that Find Clarity provides guidance on personal matters, some of your conversations may contain sensitive personal information. We treat all conversation data with the highest level of security and confidentiality.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">How We Use Your Information</h2>
          <p className="legal-text">We use the collected information for:</p>
          <ul className="legal-list">
            <li>Providing AI-powered guidance and responses to your questions</li>
            <li>Improving and personalizing your experience</li>
            <li>Analyzing usage patterns to enhance app functionality</li>
            <li>Sending important updates about the service (with your consent)</li>
            <li>Ensuring app security and preventing misuse</li>
            <li>Complying with legal obligations under Indian law</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Data Storage and Security</h2>
          <p className="legal-text">
            We implement industry-standard security measures to protect your data:
          </p>
          <ul className="legal-list">
            <li>End-to-end encryption for all conversations</li>
            <li>Secure servers with regular security audits</li>
            <li>Data stored in compliance with Indian data protection regulations</li>
            <li>Access controls limiting who can view your information</li>
            <li>Regular backups to prevent data loss</li>
          </ul>
          <p className="legal-text">
            While we strive to protect your personal information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Data Sharing and Disclosure</h2>
          <p className="legal-text">
            We do NOT sell, trade, or rent your personal information to third parties. We may share your information only in these limited circumstances:
          </p>
          <ul className="legal-list">
            <li><strong>AI Service Providers:</strong> We use third-party AI services to power our guidance features. These providers are bound by strict confidentiality agreements.</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government authority in India.</li>
            <li><strong>Safety & Protection:</strong> To protect the rights, property, or safety of Find Clarity, our users, or others.</li>
            <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition (users will be notified).</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Your Rights Under Indian Law</h2>
          <p className="legal-text">
            Under applicable Indian data protection laws, you have the right to:
          </p>
          <ul className="legal-list">
            <li>Access your personal data that we hold</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data (subject to legal retention requirements)</li>
            <li>Withdraw consent for data processing</li>
            <li>Export your data in a portable format</li>
            <li>Lodge a complaint with relevant authorities</li>
          </ul>
          <p className="legal-text">
            To exercise these rights, contact us at: <a href="mailto:shivam@findclarity.app" className="legal-link">shivam@findclarity.app</a>
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Data Retention</h2>
          <p className="legal-text">
            We retain your personal information only as long as necessary to provide our services and comply with legal obligations. Conversation data is retained to improve AI responses but can be deleted upon request.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Children's Privacy</h2>
          <p className="legal-text">
            Find Clarity is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Cookies and Tracking</h2>
          <p className="legal-text">
            We use minimal cookies and similar tracking technologies to improve user experience. You can control cookie settings through your device or browser settings.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">International Data Transfers</h2>
          <p className="legal-text">
            Your data is primarily stored and processed in India. If data is transferred internationally, we ensure appropriate safeguards are in place to protect your information.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Changes to This Privacy Policy</h2>
          <p className="legal-text">
            We may update this Privacy Policy periodically. We will notify you of significant changes via the app or email. Continued use of Find Clarity after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="legal-section">
          <h2 className="legal-heading">Contact Us</h2>
          <p className="legal-text">
            If you have questions or concerns about this Privacy Policy, please contact us:
          </p>
          <ul className="legal-list">
            <li>Email: <a href="mailto:shivam@findclarity.app" className="legal-link">shivam@findclarity.app</a></li>
            <li>We will respond to your inquiry within 30 days as required by Indian law</li>
          </ul>
        </section>

        <section className="legal-section">
          <p className="legal-disclaimer">
            <strong>Note:</strong> This Privacy Policy is drafted to comply with Indian data protection regulations including the Information Technology Act, 2000 and related rules. Please consult with a legal professional for final review and validation.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
